<template>
    <div :style="rootStyle">
        <!-- Title row -->
        <div :style="titleRowStyle">
            <div :style="titleColStyle">
                <div :style="titleLineStyle">
                    <h1 :style="h1Style">Deals</h1>
                    <span class="tag-mono" :style="countStyle">{{ totalItems }}</span>
                </div>
                <div v-if="hasStats" :style="statsStyle">
                    <span v-if="totalInvoiced">Total invoiced <b class="tag-mono tag-tnum" :style="statValueStyle">{{ totalInvoiced }}</b></span>
                    <span v-if="totalInvoiced && outstanding" :style="statSepStyle" />
                    <span v-if="outstanding">Outstanding <b class="tag-mono tag-tnum" :style="statValueStyle">{{ outstanding }}</b></span>
                    <span v-if="outstanding && commissionMtd" :style="statSepStyle" />
                    <span v-if="commissionMtd">Commission MTD <b class="tag-mono tag-tnum" :style="{ color: 'oklch(0.52 0.13 155)' }">{{ commissionMtd }}</b></span>
                </div>
            </div>
            <div :style="actionsStyle">
                <a v-if="exportHref" :href="exportHref" class="btn btn--ghost btn--sm" :style="{ gap: '5px' }">
                    <Icon name="download" /> Export
                </a>
                <button type="button" class="btn btn--subtle btn--sm" :style="{ gap: '5px' }" @click="$emit('edit-columns')">
                    <Icon name="columns" /> Columns
                </button>
                <a v-if="addHref" :href="addHref" class="btn btn--primary btn--sm" :style="{ gap: '5px' }">
                    <Icon name="plus" /> New deal
                </a>
            </div>
        </div>

        <!-- Search row (agency + brand) -->
        <div :style="searchRowStyle">
            <div v-if="showAgencySearch" :style="searchWrapStyle">
                <span :style="searchIconStyle"><Icon name="search" /></span>
                <input
                    class="input"
                    placeholder="Agency…"
                    :style="searchInputStyle"
                    :value="searchAgency"
                    @input="$emit('search-agency', $event.target.value)"
                />
            </div>
            <div :style="searchWrapStyle">
                <span :style="searchIconStyle"><Icon name="search" /></span>
                <input
                    class="input"
                    placeholder="Brand…"
                    :style="searchInputStyle"
                    :value="searchBrand"
                    @input="$emit('search-brand', $event.target.value)"
                />
            </div>
            <span :style="{ flex: '1 1 auto' }" />
            <button class="btn btn--sm btn--ghost" type="button" :style="{ gap: '5px' }">
                <Icon name="sort" /> Sort
            </button>
        </div>

        <!-- Status chips -->
        <div :style="statusRowStyle">
            <span :style="statusEyebrowStyle">Status</span>
            <button
                type="button"
                class="btn btn--xs"
                :style="chipStyle(isAllActive)"
                @click="$emit('status-all')"
            >All</button>
            <button
                v-for="s in statuses"
                :key="s.id"
                type="button"
                class="btn btn--xs"
                :style="chipStyle(selectedStatuses.map(String).includes(String(s.id)))"
                @click="$emit('status-toggle', s.id)"
            >{{ s.name }}</button>
            <button
                type="button"
                class="btn btn--xs"
                :style="chipStyle(withoutSales)"
                @click="$emit('toggle-without-sales')"
            >Without sales</button>
        </div>
    </div>
</template>

<script>
import Icon from "../../Agencies/_atoms/Icon.vue";

export default {
    name: "DealsHeader",
    components: { Icon },
    props: {
        totalItems: { type: [Number, String], default: 0 },
        statuses: { type: Array, default: () => [] },
        selectedStatuses: { type: Array, default: () => [] },
        withoutSales: { type: Boolean, default: false },
        searchAgency: { type: String, default: "" },
        searchBrand: { type: String, default: "" },
        showAgencySearch: { type: Boolean, default: true },
        exportHref: { type: String, default: null },
        addHref: { type: String, default: null },
        totalInvoiced: { type: String, default: "" },
        outstanding: { type: String, default: "" },
        commissionMtd: { type: String, default: "" },
    },
    computed: {
        isAllActive() {
            return this.selectedStatuses.length === 0 && !this.withoutSales;
        },
        hasStats() {
            return !!(this.totalInvoiced || this.outstanding || this.commissionMtd);
        },
        rootStyle() {
            return {
                padding: "18px 24px 12px",
                borderBottom: "1px solid var(--border-faint)",
                background: "var(--surface)",
                flex: "0 0 auto",
            };
        },
        titleRowStyle() {
            return { display: "flex", alignItems: "flex-end", gap: "16px", marginBottom: "14px" };
        },
        titleColStyle() {
            return { flex: "1 1 auto" };
        },
        titleLineStyle() {
            return { display: "flex", alignItems: "baseline", gap: "10px", marginBottom: "2px" };
        },
        h1Style() {
            return { font: "600 22px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 };
        },
        countStyle() {
            return { fontSize: "13px", color: "var(--text-faint)" };
        },
        statsStyle() {
            return { display: "flex", alignItems: "center", gap: "14px", fontSize: "12px", color: "var(--text-muted)" };
        },
        statValueStyle() {
            return { color: "var(--text)" };
        },
        statSepStyle() {
            return { width: "1px", height: "10px", background: "var(--border-strong)", display: "inline-block" };
        },
        actionsStyle() {
            return { display: "flex", alignItems: "center", gap: "8px" };
        },
        searchRowStyle() {
            return { display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" };
        },
        searchWrapStyle() {
            return { position: "relative", flex: "0 0 220px" };
        },
        searchIconStyle() {
            return {
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-faint)",
                display: "inline-flex",
            };
        },
        searchInputStyle() {
            return { height: "32px", paddingLeft: "32px", fontSize: "13px" };
        },
        statusRowStyle() {
            return { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" };
        },
        statusEyebrowStyle() {
            return {
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-faint)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginRight: "4px",
            };
        },
    },
    methods: {
        chipStyle(active) {
            return {
                height: "26px",
                padding: "0 9px",
                gap: "6px",
                background: active ? "var(--text)" : "transparent",
                color: active ? "var(--text-inv)" : "var(--text-muted)",
                borderColor: active ? "var(--text)" : "var(--border)",
                fontWeight: active ? 500 : 400,
            };
        },
    },
};
</script>
